import { fetchAuthSession, getCurrentUser, signIn, signOut } from 'aws-amplify/auth'
import { errorService } from '../error/errorService'

/**
 * Consolidated Authentication Service
 * Handles all authentication operations with support for both direct parameters
 * and object parameters for backward compatibility
 */
export const authService = {
  /**
   * Get current user (replaces checkAuthState)
   * @returns {Promise<object|null>} The current user or null
   */
  async getCurrentUser() {
    try {
      const currentUser = await getCurrentUser()
      return currentUser
    } catch {
      // User is not signed in or Amplify not configured
      return null
    }
  },

  /**
   * Check auth state - alias for getCurrentUser for backward compatibility
   * @returns {Promise<object|null>} The current user or null
   */
  async checkAuthState() {
    return this.getCurrentUser()
  },

  /**
   * Sign in a user - supports both parameter formats
   * @param {string|Object} usernameOrCredentials - Username or credentials object
   * @param {string} [password] - Password (when first param is string)
   * @returns {Promise<object>} Sign in result
   */
  async signIn(usernameOrCredentials, password) {
    try {
      let username, pass

      // Support both formats: signIn(username, password) and signIn({username, password})
      if (typeof usernameOrCredentials === 'string') {
        username = usernameOrCredentials
        pass = password
      } else {
        username = usernameOrCredentials.username
        pass = usernameOrCredentials.password
      }

      const { isSignedIn, nextStep } = await signIn({
        username,
        password: pass,
      })

      if (isSignedIn) {
        const user = await getCurrentUser()
        return { user, isSignedIn, nextStep }
      }

      return { isSignedIn, nextStep }
    } catch (err) {
      // Use proper error service instead of console.error
      errorService.logError(err, { context: 'Authentication', operation: 'sign-in' })
      throw err
    }
  },

  /**
   * Sign out the current user
   * @returns {Promise<Object>} Success status
   */
  async signOut() {
    try {
      await signOut()
      return { success: true }
    } catch (err) {
      // Use proper error service instead of console.error
      errorService.logError(err, {
        context: 'Authentication',
        operation: 'sign-out',
      })
      throw err
    }
  },

  /**
   * Get the current auth token
   * @returns {Promise<string|null>} The access token or null
   */
  async getAuthToken() {
    try {
      const { tokens } = await fetchAuthSession()
      if (tokens?.accessToken) {
        return tokens.accessToken.toString()
      }
      return null
    } catch {
      // Silently return null if not authenticated or Amplify not configured
      return null
    }
  },

  /**
   * Force a token refresh against Cognito (used after a 401)
   * @returns {Promise<string|null>} The new access token or null
   */
  async refreshSession() {
    try {
      const { tokens } = await fetchAuthSession({ forceRefresh: true })
      return tokens?.accessToken?.toString() ?? null
    } catch (err) {
      // Cognito unreachable: surface it as a request failure, not an expired session
      if (err?.name === 'NetworkError') throw err
      return null
    }
  },

  /**
   * Get the auth status - for use with React Query
   * @returns {Promise<Object>} Auth status object
   */
  async getAuthStatus() {
    try {
      const user = await this.getCurrentUser()
      let token = null

      if (user) {
        token = await this.getAuthToken()
      }

      return {
        isAuthenticated: !!user,
        user,
        token,
      }
    } catch {
      // Return default state if any error occurs
      return {
        isAuthenticated: false,
        user: null,
        token: null,
      }
    }
  },
}
